import { useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { routeReplace, routeTo } from "../application/actions/ui";
import { loadSolutionOverview } from "../application/actions/solutions";
import { loadChallengeById } from "../application/actions/challenges";
import { loadProfile } from "../application/actions/users";
import { loadCompany } from "../application/actions/company";
import { saveUserRole } from "../application/actions/auth";
import { setActiveTab } from "../application/actions/ui";


export default function useUrlRedirect({ cognito }) {
  const user = cognito.user
  const role = cognito.role
  const isAdmin = cognito.isAdmin
  const cognitoLoading = cognito.loading
  const dispatch = useDispatch();
  const [loading, setLoading] = useState()

  // Stable per-user identity. The session refreshes every ~10 minutes, handing back
  // a NEW user object with the SAME identity; keying the effect on this string (rather
  // than the object) stops it re-firing on every refresh, which would otherwise yank
  // logged-in users back to their profile (stacking history) each cycle. Only a real
  // login / logout / role change flips this and re-runs the routing.
  const userKey = user?.username ?? (user ? 'authenticated' : null)

  useLayoutEffect(() => {
    setLoading(true)

    const handleLoadProfiles = async (currentRole) => {
      let profile
      let company

      if (currentRole === 'company') {
        try {
          company = await dispatch(loadCompany())
        } catch (error) { }
      } else {
        try {
          profile = await dispatch(loadProfile())
        } catch (error) { }
      }

      setLoading(false)
      if (company?.userId) dispatch(routeTo({ view: 'companyProfile' }));
      else if (profile?.userId) dispatch(routeTo({ view: 'userProfile' }));
      else if (currentRole === 'company') dispatch(routeTo({ view: 'companyCreateProfile' }));
      else dispatch(routeTo({ view: 'userCreateProfile' }));
    }

    const url = window.location.pathname;
    const parts = url.split('/');
    const obj = parts?.[1];
    const id = parts?.[2];

    if (cognitoLoading) return

    // ReDo admins land on the normal profile so they keep notifications + chat (to receive
    // replies), with the admin broadcast composer available as an admin-only tab (id '4').
    // routeReplace so periodic session refreshes (which re-fire this effect) don't stack history.
    if (userKey && isAdmin) {
      // load the admin's own profile/company first, otherwise the normal profile tabs
      // (dashboard "My Solutions") have no userId to load from. Unlike handleLoadProfiles
      // we never bounce to a create-profile screen — admins always land on the admin tab.
      Promise.allSettled([dispatch(loadProfile()), dispatch(loadCompany())]).then(() => {
        setLoading(false)
        dispatch(setActiveTab('4'))
        dispatch(routeReplace({ view: 'userProfile' }))
      })
      return
    }

    // A shared link may point at something deleted, or the load may simply fail -
    // without this, the spinner above would hang forever since nothing else would
    // ever call setLoading(false) or dispatch a route.
    const onLoadProjectError = (error) => {
      console.error('could not load shared link target', error)
      if (userKey) {
        // same fallback as the default case below: a logged-in user needs a saved
        // role to land on their profile; otherwise just stop the spinner
        role ? handleLoadProfiles(role) : setLoading(false)
      } else {
        setLoading(false)
        dispatch(routeTo({ view: 'landing' }))
      }
    }

    switch (obj) {
      case 'solution':
        dispatch(loadSolutionOverview(id)).then((solution) => {
          setLoading(false)
          if (userKey) {
            dispatch(routeTo({ view: 'solution', payload: { solution } }));
          } else {
            dispatch(routeTo({ view: 'signup', payload: { project: solution } }));
          }
        }).catch(onLoadProjectError)
        break;
      case 'challenge':
        dispatch(loadChallengeById(id)).then((challenge) => {
          setLoading(false)
          if (userKey) {
            dispatch(routeTo({ view: 'challenge', payload: { challenge } }));
          } else {
            dispatch(routeTo({ view: 'signup', payload: { project: challenge } }));
          }
        }).catch(onLoadProjectError)
        break;
      case 'company':
        dispatch(saveUserRole(obj)).then(() => {
          setLoading(false)
          if (!userKey) {
            dispatch(routeTo({ view: 'login', payload: { scope: 'company' } }))
          } else {
            handleLoadProfiles('company')
          }
        })
        break;
      case 'user':
        dispatch(saveUserRole(obj)).then(() => {
          setLoading(false)
          if (!userKey) {
            dispatch(routeTo({ view: 'login', payload: { scope: 'user' } }))
          } else {
            handleLoadProfiles('user')
          }
        })
        break;
      default:
        if (userKey) {
          // Logged-in users route to their profile (needs a saved role); a rare
          // logged-in-without-role state just stops the spinner rather than hanging.
          role ? handleLoadProfiles(role) : setLoading(false)
        } else {
          // Logged-out visitors at the root land on the public landing page.
          // routeReplace avoids stacking history on the periodic session re-run.
          setLoading(false)
          dispatch(routeReplace({ view: 'landing' }))
        }
        break;
    }
  }, [dispatch, userKey, role, isAdmin, cognitoLoading]);

  return { loading }
}